'use client';
import React, { useEffect, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';
import { createClient } from '@/lib/supabase/client';

interface PricingPlan {
  name: string;
  price: string;
  reviews: string;
  highlight: boolean;
  color: string;
  bg: string;
  cta: string;
  desc: string;
  features: string[];
}

const DEFAULT_PLANS: PricingPlan[] = [
  {
    name: 'Starter', price: '₹5,000', reviews: '5–8', highlight: false,
    color: '#1A237E', bg: '#EEF0FB', cta: 'Get Started',
    desc: 'Perfect for brands submitting their first product for review and testing.',
    features: ['5–8 verified reviewers', 'AI quality check', 'Basic dashboard access', 'GST invoice included', 'Amazon or Flipkart', 'Email support'],
  },
  {
    name: 'Growth', price: '₹15,000', reviews: '15–20', highlight: true,
    color: '#FF5722', bg: '#FFF3EE', cta: 'Start Growth Plan',
    desc: 'Most popular. Ideal for brands wanting meaningful review volume and honest feedback.',
    features: ['15–20 verified reviewers', 'AI quality check', 'Advanced analytics dashboard', 'GST invoice included', 'All marketplaces supported', 'Priority support', 'Reviewer demographic targeting', 'City-wise distribution'],
  },
  {
    name: 'Enterprise', price: 'Custom', reviews: '50+', highlight: false,
    color: '#8B5CF6', bg: '#F5F3FF', cta: 'Contact Sales',
    desc: 'For brands needing 50+ reviewers, custom targeting, and dedicated review management.',
    features: ['50+ verified reviewers', 'Dedicated review manager', 'Custom reviewer targeting', 'Creator/influencer slots', 'Multi-marketplace support', 'White-label reports', 'API access', 'SLA guarantee'],
  },
];

export default function PricingSection() {
  const [plans, setPlans] = useState<PricingPlan[]>(DEFAULT_PLANS);
  const [hovered, setHovered] = useState<number | null>(null);

  useEffect(() => {
    const supabase = createClient();
    supabase
      .from('platform_settings')
      .select('value')
      .eq('key', 'pricing_plans')
      .maybeSingle()
      .then(({ data }) => {
        if (data?.value) {
          try { setPlans(JSON.parse(data.value)); } catch {}
        }
      });
  }, []);

  return (
    <section className="section-py bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-12">
          <span className="pill-badge mb-3">Pricing</span>
          <h2 className="font-heading font-bold text-foreground mb-3 mt-3" style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)' }}>
            Transparent, No Hidden Fees
          </h2>
          <p className="text-muted-foreground text-sm max-w-lg mx-auto">
            All plans include GST invoice, AI quality scoring, and real-time feedback dashboard.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans.map((plan, i) => (
            <div
              key={plan.name}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
              className={`relative bg-white rounded-3xl border-2 p-6 sm:p-7 flex flex-col transition-all duration-300 ${
                plan.highlight
                  ? 'border-primary shadow-lg shadow-primary/10'
                  : hovered === i
                  ? 'border-border shadow-md'
                  : 'border-border'
              }`}
            >
              {plan.highlight && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                  <span className="cta-gradient text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full shadow-sm">
                    Most Popular
                  </span>
                </div>
              )}

              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: plan.bg }}>
                    <Icon name="RocketLaunchIcon" size={16} style={{ color: plan.color }} />
                  </div>
                  <h3 className="font-heading font-bold text-foreground">{plan.name}</h3>
                </div>
                <div className="font-heading font-bold text-3xl text-foreground mb-1">{plan.price}</div>
                <div className="text-xs text-muted-foreground">
                  {plan.reviews} verified reviewers · GST included
                </div>
                <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{plan.desc}</p>
              </div>

              <ul className="space-y-3 flex-1 mb-7">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-center gap-2.5 text-sm text-foreground/80">
                    <Icon name="CheckCircleIcon" size={15} className="text-accent shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>

              <Link
                href="/contact"
                className={`w-full text-center font-heading font-bold py-3 rounded-xl text-sm transition-all ${
                  plan.highlight
                    ? 'cta-gradient text-white hover:opacity-90 shadow-sm shadow-primary/20'
                    : 'border-2 hover:opacity-80'
                }`}
                style={!plan.highlight ? { color: plan.color, borderColor: `${plan.color}40`, backgroundColor: plan.bg } : {}}
              >
                {plan.cta}
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}